// src/Routes.jsx
import React, { Suspense, lazy, useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes as RouterRoutes,
  Route,
  useLocation,
} from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import RegionGate from "components/RegionGate";
import IndiaSplash from "components/IndiaSplash";
import { setupScrollReveal } from "./utils/v5Shell";
import {
  REGION_CHANGE_EVENT,
  REGIONS,
  getRegion,
  isIndiaPath,
  setRegion,
} from "./utils/region";

/* -------- Existing pages (eager) -------- */
import Contact from "pages/contact";
import AboutUs from "pages/about-us";
import Homepage from "pages/homepage";

/* -------- Lazy pages (ensure these files exist) -------- */
/* Capabilities */
const AiDataPlatforms = lazy(() => import("pages/capabilities/AiDataPlatforms"));
const MobileWebEngineering = lazy(() =>
  import("pages/capabilities/MobileWebEngineering")
);
const ComputerVisionWearables = lazy(() =>
  import("pages/capabilities/ComputerVisionWearables")
);
const IoTSensorsEdge = lazy(() =>
  import("pages/capabilities/IoTSensorsEdge")
);
const CapabilitiesIndex = lazy(() => import("pages/capabilities/index.jsx"));

/* Products */
const ByOnco = lazy(() => import("pages/products/ByOnco"));
const VayuX = lazy(() => import("pages/products/VayuX"));
const SurakshaPath = lazy(() => import("pages/products/SurakshaPath"));
const Parkingkar = lazy(() => import("pages/products/Parkingkar"));
const ProductsIndex = lazy(() => import("pages/products/index.jsx"));

/* Company */
const Careers = lazy(() => import("pages/company/Careers"));
const News = lazy(() => import("pages/company/News"));
const InsightPage = lazy(() => import("pages/insights/InsightPage"));

/* Legal (use actual filenames) */
const PrivacyPolicy = lazy(() => import("pages/legal/PrivacyPolicy"));
const Terms = lazy(() => import("pages/legal/Terms"));
const SecurityCompliance = lazy(() =>
  import("pages/legal/SecurityCompliance")
);
const Cookies = lazy(() => import("pages/legal/Cookies"));

/* Suraksha Path standalone */
const SurakshaPathLanding = lazy(() => import("pages/surakshapath/index.jsx"));

/* Simple loading fallback */
const Fallback = () => (
  <div className="min-h-[40vh] flex items-center justify-center text-muted-foreground">
    Loading…
  </div>
);

function RouteEffects() {
  const location = useLocation();

  useEffect(() => {
    // Re-run scroll reveal after the entry splash, so the first paint is not
    // competing with ScrollTrigger layout work.
    let cancelled = false;
    let timer = 0;
    const run = () => {
      if (cancelled) return;
      const ld = document.getElementById("loader");
      if (ld && ld.style.display !== "none") {
        timer = window.setTimeout(run, 160);
        return;
      }
      try {
        setupScrollReveal();
      } catch {
        // ignore if GSAP not ready yet
      }
    };
    timer = window.setTimeout(run, 80);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [location.pathname]);

  return null;
}

function HomeEntry() {
  return <Homepage />;
}

function ToIndia() {
  useEffect(() => {
    // /in is the India studio document, not this shell. Sending it back to
    // /entering-in loops the splash forever (fade out, reload, fade out).
    try {
      const navType = performance.getEntriesByType?.("navigation")?.[0]?.type;
      if (navType === "back_forward") {
        window.location.replace("/");
        return;
      }
    } catch {
      /* ignore – Safari private mode */
    }
    window.location.replace("/entering-in");
  }, []);
  return null;
}

function isHomePath(pathname) {
  return pathname === "/" || pathname === "/homepage";
}

function skipRegionGate(pathname) {
  return (
    pathname.startsWith("/surakshapath") ||
    pathname.startsWith("/legal") ||
    pathname.startsWith("/super-intelligence") ||
    pathname === "/company/praesidio-care-private-limited" ||
    pathname === "/company/global-presence" ||
    pathname === "/company/founder" ||
    pathname === "/privacy" ||
    pathname === "/entering-in" ||
    pathname === "/india"
  );
}

function RegionShell({ children }) {
  const { pathname } = useLocation();
  const [region, setRegionState] = useState(() => {
    if (typeof window === "undefined") return null;
    if (isIndiaPath(window.location.pathname)) {
      setRegion(REGIONS.IN);
      return REGIONS.IN;
    }
    // Opening the site should always offer India vs Rest of the World,
    // even if a previous visit stored a region.
    if (isHomePath(window.location.pathname)) return null;
    return getRegion();
  });

  useEffect(() => {
    if (isIndiaPath(pathname) && region !== REGIONS.IN) {
      setRegion(REGIONS.IN);
      setRegionState(REGIONS.IN);
    }
  }, [pathname, region]);

  useEffect(() => {
    const onChange = (event) => setRegionState(event.detail || getRegion());
    window.addEventListener(REGION_CHANGE_EVENT, onChange);
    return () => window.removeEventListener(REGION_CHANGE_EVENT, onChange);
  }, []);

  if (!region && !skipRegionGate(pathname)) return <RegionGate />;
  return children;
}

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouteEffects />
        <RegionShell>
        <Suspense fallback={<Fallback />}>
          <RouterRoutes>
            {/* Core */}
            <Route path="/" element={<HomeEntry />} />
            <Route path="/homepage" element={<HomeEntry />} />
            <Route path="/in" element={<ToIndia />} />
            <Route path="/in/*" element={<ToIndia />} />
            <Route path="/entering-in" element={<IndiaSplash />} />
            <Route path="/india" element={<IndiaSplash />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/contact" element={<Contact />} />

            {/* Suraksha Path standalone, www.praesidiocare.com/surakshapath */}
            <Route path="/surakshapath" element={<SurakshaPathLanding />} />

            {/* Capabilities */}
            <Route path="/capabilities" element={<CapabilitiesIndex />} />
            <Route
              path="/capabilities/ai-data-platforms"
              element={<AiDataPlatforms />}
            />
            <Route
              path="/capabilities/mobile-web-engineering"
              element={<MobileWebEngineering />}
            />
            <Route
              path="/capabilities/computer-vision-wearables"
              element={<ComputerVisionWearables />}
            />
            <Route
              path="/capabilities/iot-sensors-edge"
              element={<IoTSensorsEdge />}
            />

            {/* Products */}
            <Route path="/products" element={<ProductsIndex />} />
            <Route path="/products/byonco" element={<ByOnco />} />
            <Route path="/products/vayu-x" element={<VayuX />} />
            <Route path="/products/surakshapath" element={<SurakshaPath />} />
            <Route path="/products/parkingkar" element={<Parkingkar />} />

            {/* Company (support both /company/... and top-level aliases) */}
            <Route path="/careers" element={<Careers />} />
            <Route path="/company/careers" element={<Careers />} />
            <Route path="/news" element={<News />} />
            <Route path="/company/news" element={<News />} />
            <Route path="/company/praesidio-care-private-limited" element={<InsightPage />} />
            <Route path="/company/global-presence" element={<InsightPage />} />
            <Route path="/company/founder" element={<InsightPage />} />

            {/* Super Intelligence essays. In-page links and sitemap only. */}
            <Route path="/super-intelligence" element={<InsightPage />} />
            <Route path="/super-intelligence/:slug" element={<InsightPage />} />

            {/* Legal (support /legal/... and short aliases) */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/legal/privacy" element={<PrivacyPolicy />} />
            <Route path="/legal/terms" element={<Terms />} />
            <Route path="/legal/security" element={<SecurityCompliance />} />
            <Route path="/legal/cookies" element={<Cookies />} />

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </Suspense>
        </RegionShell>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
